import createCache from '@emotion/cache'

/**
 * Creates an Emotion CSS cache with a custom insertion point for better CSS ordering.
 *
 * This function implements a defensive, multi-layered approach to find the best place
 * to inject CSS styles in the document head. It uses multiple fallback strategies and
 * redundant safety checks to handle various edge cases in different environments
 * (SSR, client-side hydration, dynamic DOM manipulation, etc.).
 */
export default function createEmotionCache() {
  let insertionPoint = null

  // SSR Safety: Only attempt DOM manipulation on the client
  if (typeof document !== 'undefined') {
    try {
      const { head } = document

      // STRATEGY 1: Insert before the first script tag
      // This ensures CSS loads before any JavaScript that might depend on styles
      const firstScript = head.querySelector('script')

      // Redundant check: Verify the script element is actually in the DOM
      // This handles cases where querySelector might return detached elements
      if (firstScript && document.contains(firstScript)) {
        insertionPoint = firstScript
      } else {
        // STRATEGY 2: Use explicit meta tag marker (fallback)
        const metaEl = document.querySelector('meta[name="emotion-insertion-point"]')

        // Triple redundancy checks for meta tag approach:
        // 1. Element exists
        // 2. Element is in the document
        // 3. Element's parent exists and is in the document
        // This handles cases where elements might be detached or manipulated by other scripts
        if (metaEl && document.contains(metaEl)
          && metaEl.parentNode
          && document.contains(metaEl.parentNode)) {
          insertionPoint = metaEl
        }
        // If both strategies fail, insertionPoint remains null (uses Emotion's default)
      }
    } catch (error) {
      // Defensive: Catch any DOM manipulation errors (e.g., in restricted environments)
      insertionPoint = null
      throw new Error(`Failed to create Emotion cache: ${error.message}`)
    }
  }

  // Base cache configuration
  const cacheConfig = {
    key: 'css',
    speedy: false,
  }

  // Final validation before using custom insertion point
  // Additional redundancy: Re-verify insertion point is still valid before cache creation
  if (insertionPoint && typeof document !== 'undefined') {
    try {
      // Last safety check: Ensure the insertion point is still in the DOM
      // This handles cases where DOM might have changed between discovery and usage
      if (document.contains(insertionPoint)) {
        cacheConfig.insertionPoint = insertionPoint
      }
      // If validation fails, cache falls back to default insertion behavior
    } catch (error) {
      throw new Error(`Failed to create Emotion cache: ${error.message}`)
    }
  }

  return createCache(cacheConfig)
}
